import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "sonner";
import "@/App.css";
import { Loader2, Lock } from "lucide-react";

import Layout from "./components/Layout";
import { WorkspaceProvider } from "./lib/workspaceContext";
import { AuthProvider, useAuth } from "./lib/authContext";
import { IGAccountProvider } from "./lib/igContext";
import { ThemeProvider, useTheme } from "./lib/themeContext";
import { I18nProvider } from "./lib/i18nContext";
import FloatingAIAssistant from "./components/FloatingAIAssistant";

import Dashboard from "./pages/Dashboard";
import MultiComptes from "./pages/MultiComptes";
import HooksBank from "./pages/HooksBank";
import CaptionsSystem from "./pages/CaptionsSystem";
import Planning from "./pages/Planning";
import Routine from "./pages/Routine";
import Analytics from "./pages/Analytics";
import SpyConcurrence from "./pages/SpyConcurrence";
import VideoIdeas from "./pages/VideoIdeas";
import EducationalPage from "./pages/EducationalPage";
import AlgorithmsHub from "./pages/AlgorithmsHub";
import AlgorithmDetail from "./pages/AlgorithmDetail";
import StrategiesHub from "./pages/StrategiesHub";
import StrategyDetail from "./pages/StrategyDetail";
import MillionViewsLab from "./pages/MillionViewsLab";
import VARecruiting from "./pages/VARecruiting";
import CommandCenter from "./pages/CommandCenter";
import IdeaVault from "./pages/IdeaVault";
import ContentAnalyzer from "./pages/ContentAnalyzer";
import PerformancePro from "./pages/PerformancePro";
import IntelligenceHub from "./pages/IntelligenceHub";
import Login from "./pages/Login";
import Register from "./pages/Register";
import TeamMode from "./pages/TeamMode";
import SwipeFile from "./pages/SwipeFile";
import CompetitorIntel from "./pages/CompetitorIntel";
import PostingPlanner from "./pages/PostingPlanner";
// === New CRM + IG Analytics ===
import InstagramAccounts from "./pages/InstagramAccounts";
import ModelsCRM from "./pages/ModelsCRM";
import ModelDetail from "./pages/ModelDetail";
import AnalyticsPro from "./pages/AnalyticsPro";
import TasksBoard from "./pages/TasksBoard";
import AlertsCenter from "./pages/AlertsCenter";
import ContentTable from "./pages/ContentTable";
// === CDO Agency redesign — new pages ===
import CRMContacts from "./pages/CRMContacts";
import CRMPipeline from "./pages/CRMPipeline";
import CRMSegments from "./pages/CRMSegments";
import Settings from "./pages/Settings";
import Placeholder from "./pages/Placeholder";

const Spinner = () => (
  <div className="min-h-screen flex items-center justify-center" style={{ background: "var(--bg-base)" }}>
    <Loader2 className="w-8 h-8 animate-spin" style={{ color: "var(--brand-purple)" }} />
  </div>
);

function Protected({ children }) {
  const { user, checking } = useAuth();
  if (checking || user === null) return <Spinner />;
  if (user === false) return <Navigate to="/login" replace />;
  return children;
}

function AdminOnly({ children }) {
  const { user, isAdmin } = useAuth();
  if (!user) return <Navigate to="/login" replace />;
  if (!isAdmin) {
    return (
      <div className="min-h-screen flex items-center justify-center px-4" data-testid="admin-only-block" style={{ background: "var(--bg-base)" }}>
        <div className="surface p-10 max-w-md text-center">
          <Lock className="w-12 h-12 mx-auto mb-4" strokeWidth={1.2} style={{ color: "var(--text-muted)" }} />
          <div className="text-[10px] font-mono uppercase tracking-[0.2em] mb-2" style={{ color: "var(--status-warning)" }}>// ACCÈS RESTREINT</div>
          <h1 className="font-display text-2xl mb-2" style={{ color: "var(--text-primary)" }}>Réservé administrateur</h1>
          <p className="text-sm" style={{ color: "var(--text-secondary)" }}>Cette section est accessible uniquement à l'admin propriétaire du site.</p>
          <a href="/" className="inline-block mt-5 text-xs font-mono uppercase tracking-wider hover:underline" style={{ color: "var(--brand-purple)" }}>← Retour au dashboard</a>
        </div>
      </div>
    );
  }
  return children;
}

function PublicOnly({ children }) {
  const { user, checking } = useAuth();
  if (checking || user === null) return <Spinner />;
  if (user) return <Navigate to="/" replace />;
  return children;
}

const Admin = ({ children }) => <AdminOnly>{children}</AdminOnly>;

// Bridge theme lang -> I18nProvider
function I18nBridge({ children }) {
  const { lang } = useTheme() || { lang: "fr" };
  return <I18nProvider lang={lang}>{children}</I18nProvider>;
}

function AppShell() {
  return (
    <Protected>
      <WorkspaceProvider>
        <IGAccountProvider>
          <Layout>
            <Routes>
              {/* User-accessible (admin + viewer) */}
              <Route path="/" element={<Dashboard />} />
              <Route path="/routine" element={<Routine />} />
              <Route path="/analytics" element={<Analytics />} />
              <Route path="/analytics-pro" element={<AnalyticsPro />} />
              <Route path="/settings" element={<Settings />} />
              <Route path="/instagram-accounts" element={<Admin><InstagramAccounts /></Admin>} />
              <Route path="/models" element={<Admin><ModelsCRM /></Admin>} />
              <Route path="/models/:id" element={<Admin><ModelDetail /></Admin>} />
              <Route path="/tasks-board" element={<TasksBoard />} />
              <Route path="/alerts" element={<AlertsCenter />} />
              <Route path="/content-table" element={<ContentTable />} />

              {/* CDO Agency — Social CRM (admin) */}
              <Route path="/crm/contacts" element={<Admin><CRMContacts /></Admin>} />
              <Route path="/crm/pipeline" element={<Admin><CRMPipeline /></Admin>} />
              <Route path="/crm/segments" element={<Admin><CRMSegments /></Admin>} />
              <Route path="/crm/inbox" element={<Admin><Placeholder title="Boîte de réception DM" subtitle="Centralise tes messages directs Instagram / TikTok / X." badge="BIENTÔT" /></Admin>} />
              <Route path="/crm/partnerships" element={<Admin><Placeholder title="Partenariats & Collab" subtitle="Suis tes deals, contrats et collaborations en cours." badge="BIENTÔT" /></Admin>} />

              {/* CDO Agency — Stratégie & Contenu placeholders */}
              <Route path="/carousels" element={<Admin><Placeholder title="Templates Carousels" subtitle="Bibliothèque de carousels structurés, hooks et CTA." badge="BIENTÔT" /></Admin>} />
              <Route path="/stories" element={<Admin><Placeholder title="Story Strategy" subtitle="Templates et frameworks pour stories à forte rétention." badge="BIENTÔT" /></Admin>} />

              {/* Admin-only — full marketing OS */}
              <Route path="/comptes" element={<Admin><MultiComptes /></Admin>} />
              <Route path="/hooks-bank" element={<Admin><HooksBank /></Admin>} />
              <Route path="/captions" element={<Admin><CaptionsSystem /></Admin>} />
              <Route path="/planning" element={<Admin><Planning /></Admin>} />
              <Route path="/spy" element={<Admin><SpyConcurrence /></Admin>} />
              <Route path="/video-ideas" element={<Admin><VideoIdeas /></Admin>} />
              <Route path="/algorithmes" element={<Admin><AlgorithmsHub /></Admin>} />
              <Route path="/algorithmes/:platform" element={<Admin><AlgorithmDetail /></Admin>} />
              <Route path="/strategies-plateformes" element={<Admin><StrategiesHub /></Admin>} />
              <Route path="/strategies-plateformes/:platform" element={<Admin><StrategyDetail /></Admin>} />
              <Route path="/million-views-lab" element={<Admin><MillionViewsLab /></Admin>} />
              <Route path="/va-recruiting" element={<Admin><VARecruiting /></Admin>} />
              <Route path="/command-center" element={<Admin><CommandCenter /></Admin>} />
              <Route path="/idea-vault" element={<Admin><IdeaVault /></Admin>} />
              <Route path="/content-analyzer" element={<Admin><ContentAnalyzer /></Admin>} />
              <Route path="/performance-pro" element={<Admin><PerformancePro /></Admin>} />
              <Route path="/intelligence-hub" element={<Admin><IntelligenceHub /></Admin>} />
              <Route path="/team-mode" element={<Admin><TeamMode /></Admin>} />
              <Route path="/swipe-file" element={<Admin><SwipeFile /></Admin>} />
              <Route path="/competitor-intel" element={<Admin><CompetitorIntel /></Admin>} />
              <Route path="/posting-planner" element={<Admin><PostingPlanner /></Admin>} />

              {/* Educational pages — admin only */}
              <Route path="/warmup" element={<Admin><EducationalPage /></Admin>} />
              <Route path="/scaling" element={<Admin><EducationalPage /></Admin>} />
              <Route path="/shadowban" element={<Admin><EducationalPage /></Admin>} />
              <Route path="/team" element={<Admin><EducationalPage /></Admin>} />
              <Route path="/watchtime" element={<Admin><EducationalPage /></Admin>} />
              <Route path="/errors" element={<Admin><EducationalPage /></Admin>} />
              <Route path="/reels" element={<Admin><EducationalPage /></Admin>} />
              <Route path="/trends-strat" element={<Admin><EducationalPage /></Admin>} />
              <Route path="/storytelling" element={<Admin><EducationalPage /></Admin>} />
              <Route path="/story" element={<Admin><EducationalPage /></Admin>} />
              <Route path="/comment-baiting" element={<Admin><EducationalPage /></Admin>} />
              <Route path="/engagement" element={<Admin><EducationalPage /></Admin>} />
              <Route path="/dm" element={<Admin><EducationalPage /></Admin>} />
              <Route path="/hooks-viraux" element={<Admin><EducationalPage /></Admin>} />
              <Route path="/tests" element={<Admin><EducationalPage /></Admin>} />
              <Route path="/organisation" element={<Admin><EducationalPage /></Admin>} />
              <Route path="/automation" element={<Admin><EducationalPage /></Admin>} />
              <Route path="/growth-hacking" element={<Admin><EducationalPage /></Admin>} />
              <Route path="/branding" element={<Admin><EducationalPage /></Admin>} />

              {/* Fallback */}
              <Route path="*" element={<Placeholder title="Page introuvable" subtitle="Cette route n'existe pas encore — utilise la barre latérale pour naviguer." badge="404" />} />
            </Routes>
          </Layout>
          <FloatingAIAssistant />
        </IGAccountProvider>
      </WorkspaceProvider>
    </Protected>
  );
}

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <ThemeProvider>
          <I18nBridge>
            <AuthProvider>
              <Routes>
                <Route path="/login" element={<PublicOnly><Login /></PublicOnly>} />
                <Route path="/register" element={<PublicOnly><Register /></PublicOnly>} />
                <Route path="/*" element={<AppShell />} />
              </Routes>
            </AuthProvider>
            <Toaster position="top-right" toastOptions={{
              style: { background: "var(--bg-surface)", border: "1px solid var(--border-default)", color: "var(--text-primary)", fontFamily: "Inter, sans-serif" }
            }} />
          </I18nBridge>
        </ThemeProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;
